import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ShieldCheck, RefreshCw, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { tierLabel } from '@/lib/tiers';

export default function AdminSubscriptionsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Reconciliation sweep state — hits POST /admin/subscriptions/reconcile,
  // which walks every user with `subscription_status=active` +
  // paid-tier and asks PayPal whether the sub is really still active.
  // Auto-heals mismatches (missing paypal_subscription_id, cancelled at
  // PayPal, etc.). Introduced 05 Aug 2026 after Stacie Hurst hit the
  // same zombie state as Lauren Stemen (27 Jul 2026).
  const [reconciling, setReconciling] = useState(false);
  const [reconcileReport, setReconcileReport] = useState(null);

  const runReconcile = async () => {
    if (!window.confirm(
      'Scan every active subscription against PayPal and auto-heal any mismatch?\n\n' +
      'Safe / idempotent — will downgrade users whose PayPal sub is no longer active. ' +
      'Runs one PayPal API call per active-subscriber user.'
    )) return;
    setReconciling(true);
    setReconcileReport(null);
    try {
      const { data: r } = await api.post('/admin/subscriptions/reconcile');
      setReconcileReport(r);
      const healed = (r.counts?.healed_missing_paypal_id || 0) + (r.counts?.healed_paypal_inactive || 0);
      if (healed > 0) {
        toast.success(`Reconciled — healed ${healed} user${healed === 1 ? '' : 's'}.`);
      } else {
        toast.success(`All ${r.counts?.checked || 0} active subs are in sync with PayPal.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reconcile failed. Check server logs.');
    } finally {
      setReconciling(false);
    }
  };

  useEffect(() => {
    api.get('/admin/subscriptions').then(({ data: d }) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin w-8 h-8 border-4 border-[#b456df] border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="admin-subscriptions-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center gap-3 mb-8">
        <Link to="/admin"><Button variant="ghost" size="sm" className="rounded-full"><ArrowLeft className="w-4 h-4" /></Button></Link>
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Subscriptions</h1>
          <p className="text-slate-500 mt-1">Manage all subscriptions and view revenue</p>
        </div>
      </div>

      {/* Reconciliation sweep — one-click "make our DB match PayPal reality"
          button. See the state-comment above for the backstory. */}
      <Card className="border-slate-100 mb-8" data-testid="admin-reconcile-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <ShieldCheck className="w-5 h-5 text-[#56dfb4]" /> PayPal Reconciliation Sweep
          </CardTitle>
          <Button
            onClick={runReconcile}
            disabled={reconciling}
            className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] gap-2"
            data-testid="admin-reconcile-run-btn"
          >
            <RefreshCw className={`w-4 h-4 ${reconciling ? 'animate-spin' : ''}`} />
            {reconciling ? 'Scanning…' : 'Run sweep'}
          </Button>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 mb-3">
            Cross-checks every user currently marked <code className="text-xs bg-slate-100 px-1 rounded">subscription_status=active</code>
            {' '}against PayPal. Auto-heals mismatches (missing PayPal ID or subscription cancelled at PayPal).
            Run this any time a user reports they were charged after cancelling.
          </p>
          {reconcileReport && (
            <div className="space-y-3" data-testid="admin-reconcile-report">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                {[
                  ['Checked', reconcileReport.counts?.checked || 0, 'bg-slate-50 text-slate-700 border-slate-200'],
                  ['In sync', reconcileReport.counts?.synced || 0, 'bg-emerald-50 text-emerald-700 border-emerald-200'],
                  ['Skipped (comp/coupon)', reconcileReport.counts?.skipped_non_paypal_grant || 0, 'bg-slate-50 text-slate-500 border-slate-200'],
                  ['Healed (no PP ID)', reconcileReport.counts?.healed_missing_paypal_id || 0, 'bg-amber-50 text-amber-800 border-amber-200'],
                  ['Healed (PP inactive)', reconcileReport.counts?.healed_paypal_inactive || 0, 'bg-amber-50 text-amber-800 border-amber-200'],
                  ['PayPal errors', reconcileReport.counts?.paypal_lookup_failed || 0, 'bg-red-50 text-red-700 border-red-200'],
                ].map(([label, count, cls]) => (
                  <div key={label} className={`rounded-xl border p-3 text-center ${cls}`}>
                    <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{count}</p>
                    <p className="text-[11px] uppercase tracking-wider">{label}</p>
                  </div>
                ))}
              </div>
              {(reconcileReport.results || []).filter(r => r.action !== 'synced' && r.action !== 'skipped_non_paypal_grant').length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> Users healed / flagged
                  </p>
                  <div className="rounded-xl border border-slate-100 divide-y divide-slate-100 overflow-hidden">
                    {reconcileReport.results.filter(r => r.action !== 'synced' && r.action !== 'skipped_non_paypal_grant').map((r) => (
                      <div key={r.user_id} className="p-2.5 text-xs flex items-center justify-between gap-3" data-testid={`reconcile-row-${r.user_id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-800 truncate">{r.email}</p>
                          <p className="text-slate-500 truncate">
                            {r.previous_tier} → <strong>{r.new_tier}</strong>
                            {r.paypal_status ? <> · PayPal: <span className="font-mono">{r.paypal_status}</span></> : null}
                            {r.error ? <> · <span className="text-red-600 font-mono">{r.error}</span></> : null}
                          </p>
                        </div>
                        <Badge className="rounded-full text-[10px] uppercase" variant="outline">
                          {r.action.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tier Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {Object.entries(data?.tier_stats || {}).map(([tier, count]) => (
          <Card key={tier} className="border-slate-100" data-testid={`admin-tier-stat-${tier}`}>
            <CardContent className="p-5 text-center">
              <p className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{count}</p>
              <p className="text-sm text-slate-500 mt-1">{tierLabel(tier)} users</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Transactions */}
      <Card className="border-slate-100">
        <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Payment Transactions</CardTitle></CardHeader>
        <CardContent className="p-0">
          {data?.transactions?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.transactions.map((t, i) => (
                  <TableRow key={t.session_id || `txn-${i}`}>
                    <TableCell className="text-sm">{t.email}</TableCell>
                    <TableCell className="capitalize text-sm">{t.plan_id}</TableCell>
                    <TableCell className="text-sm">${t.amount}</TableCell>
                    <TableCell>
                      <Badge variant={t.payment_status === 'paid' ? 'default' : 'secondary'} className="rounded-full text-xs capitalize">
                        {t.payment_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{t.created_at ? new Date(t.created_at).toLocaleDateString() : 'N/A'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center text-slate-400 py-10">No transactions yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
